import { NOTIFICATION_TYPE, REQUEST_STATUS } from "@src/constants";
import { AlertConfig, RequestOverviewDetails } from "@src/types";
import { Allocation, RoleRequest } from "@src/types/role_request_types";

/**
 * Creates an alert configuration object.
 *
 * @param condition     The condition to check if the alert should be shown
 * @param title         The title of the alert
 * @param text          The text of the alert
 * @param type          The type of the alert
 * @param action        The action to perform when the alert is shown
 * @return              the alert config based on the given information
 */
export function createAlertConfig(
  condition: boolean,
  title: string,
  text: React.ReactElement | string,
  type: NOTIFICATION_TYPE.INFO | NOTIFICATION_TYPE.WARNING,
  action: React.ReactNode = null
): AlertConfig {
  return {
    condition,
    message: (
      <>
        <b>{title}</b> - {text}
      </>
    ),
    type,
    action,
  };
}

/**
 * Creates a list with all alert configs for the team lead
 *
 * @param request the request to get the condition for the alert
 * @returns a list with all the alert configs
 */
export function getStaffingRequestAlertConfig(
  request: RequestOverviewDetails
): AlertConfig[] {
  return [
    createAlertConfig(
      request?.childRequest?.isOutdated ?? request?.isOutdated,
      "REQUEST OUTDATED",
      "This request is outdated, please get in contact with the project lead.",
      NOTIFICATION_TYPE.WARNING
    ),
    createAlertConfig(
      request?.isEmployeeDeleted,
      "EMPLOYEE DELETED",
      "The formerly assigned employee has been deleted, and the request was set back to the status requested.",
      NOTIFICATION_TYPE.INFO
    ),
    createAlertConfig(
      request?.isDeclined && !!request?.assignedTeamMember,
      "PROPOSAL DECLINED",
      `The proposed team member "${request?.assignedTeamMember}" was declined. Please respond to this request again.`,
      NOTIFICATION_TYPE.WARNING
    ),
    createAlertConfig(
      request?.isHandedOver,
      "REQUEST HANDED OVER",
      "This request has been handed over to you from another team lead.",
      NOTIFICATION_TYPE.INFO
    ),
    createAlertConfig(
      request?.isTlChangedOfAssignedEmployee,
      "REQUEST CHANGED",
      "The already confirmed team member assignment to this project request has been set back to the 'requested' status due to changes in team set-ups and team lead responsibilities.",
      NOTIFICATION_TYPE.INFO
    ),
    createAlertConfig(
      request?.childRequest && !request.isOnlyFreeTextFieldChanged,
      "REQUEST CHANGED",
      "This request has been changed. Please review the changes and accept them.",
      NOTIFICATION_TYPE.WARNING
    ),
    createAlertConfig(
      request?.isDeclined && !request?.assignedTeamMember,
      "PROPOSAL DECLINED",
      `Please respond to this request again. If you don’t know why your request has been declined, please contact the requester (${request?.sendBy?.fullName}).`,
      NOTIFICATION_TYPE.WARNING
    ),
  ];
}

/**
 * Creates a list with all alert configs for the project lead
 *
 * @param request the request to get the condition for the alert
 * @returns a list with all the alert configs
 */
export function getRoleRequestAlertConfigs(
  request: RoleRequest
): AlertConfig[] {
  return [
    createAlertConfig(
      request?.isProposalWithdrawn,
      "PROPOSAL TAKEN BACK",
      "The initially proposed team member has been taken back by the team lead.",
      NOTIFICATION_TYPE.INFO
    ),
    createAlertConfig(
      request?.isEmployeeDeleted,
      "EMPLOYEE DELETED",
      "The formerly assigned employee has been deleted, and the request was set back to the status requested.",
      NOTIFICATION_TYPE.INFO
    ),
    createAlertConfig(
      request?.isOutdated,
      "REQUEST OUTDATED",
      "This request is outdated. Please update or delete it.",
      NOTIFICATION_TYPE.WARNING
    ),
    createAlertConfig(
      request?.status === REQUEST_STATUS.PROPOSED &&
        request?.allocationDetails.some(
          (allocation: Allocation) => allocation.proposedPercentage != null
        ),
      "REQUEST CHANGED",
      "The team lead has suggested a new workload for this request. Please get in touch with the team lead if necessary.",
      NOTIFICATION_TYPE.WARNING
    ),
    createAlertConfig(
      request?.status === REQUEST_STATUS.DECLINED &&
        request?.allocationDetails.every(
          (allocation: Allocation) => allocation.proposedPercentage == null
        ),
      "REQUEST DECLINED",
      "This request is declined, please get in touch with the team lead if necessary.",
      NOTIFICATION_TYPE.WARNING
    ),
    createAlertConfig(
      request?.status === REQUEST_STATUS.DECLINED &&
        request?.allocationDetails.some(
          (allocation: Allocation) => allocation.proposedPercentage != null
        ),
      "REQUEST DECLINED",
      <>
        This request is <b>declined</b> and team lead has{" "}
        <b>suggested a new workload</b>, please get in touch with the team lead
        if necessary.
      </>,
      NOTIFICATION_TYPE.WARNING
    ),
  ];
}
